import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Title,
  Group,
  Button,
  TextInput,
  Text,
  Badge,
  Stack,
  Alert,
  LoadingOverlay,
  Grid,
  ActionIcon,
  Modal,
  NumberInput,
} from '@mantine/core';
import { useSupabase } from '../contexts/SupabaseContext';
import {
  IconPlayerPause,
  IconPlayerPlay,
  IconCoins,
  IconLock,
  IconRefresh,
  IconAlertCircle,
  IconSearch,
  IconCheck,
} from '@tabler/icons-react';

const TokenControl = () => {
  const { isConnected: supabaseConnected, error: supabaseError } = useSupabase();
  const [loading, setLoading] = useState(false);
  const [paused, setPaused] = useState(false);
  const [balance, setBalance] = useState('0');

  // 권한 확인 (임시로 모든 접근 허용)
  const hasAccess = true;
  const [lockedAmount, setLockedAmount] = useState('0');
  const [queryAddress, setQueryAddress] = useState('');
  const [queryModalOpened, setQueryModalOpened] = useState(false);
  const [queryResult, setQueryResult] = useState(null);

  const loadTokenStatus = async () => {
    setLoading(true);
    try {
      // TODO: 실제 컨트랙트에서 데이터 로드
      // 임시 데이터
      setPaused(false);
      setBalance('1,000,000,000');
      setLockedAmount('250,000,000');
    } catch (error) {
      console.error('Error loading token status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async () => {
    try {
      // TODO: 실제 컨트랙트에서 pause 실행
      console.log('Pausing token');
      setPaused(true);
    } catch (error) {
      console.error('Error pausing token:', error);
    }
  };

  const handleUnpause = async () => {
    try {
      // TODO: 실제 컨트랙트에서 unpause 실행
      console.log('Unpausing token');
      setPaused(false);
    } catch (error) {
      console.error('Error unpausing token:', error);
    }
  };

  const handleQueryBalance = async () => {
    if (!queryAddress) return;
    
    try {
      // TODO: 실제 컨트랙트에서 balanceOf 조회
      console.log('Querying balance for:', queryAddress);
      setQueryResult({
        address: queryAddress,
        balance: '1,500,000',
        lockedAmount: '100,000',
      });
    } catch (error) {
      console.error('Error querying balance:', error);
    }
  };

  const handleQueryLockedAmount = async () => {
    if (!queryAddress) return;
    
    try {
      // TODO: 실제 컨트랙트에서 lockedAmount 조회
      console.log('Querying locked amount for:', queryAddress);
      setQueryResult({
        address: queryAddress,
        balance: '1,500,000',
        lockedAmount: '100,000',
      });
    } catch (error) {
      console.error('Error querying locked amount:', error);
    }
  };

  useEffect(() => {
    loadTokenStatus();
  }, []);

  // 권한이 없는 경우 접근 거부 메시지 표시
  if (!hasAccess) {
    return (
      <Container fluid size="xl" py="md">
        <Alert icon={<IconAlertCircle size="1rem" />} color="red" radius="md">
          <Title order={3} mb="sm">Access Denied</Title>
          <Text>
            You need to connect your wallet and have Owner or Manager privileges to access this page.
          </Text>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid size="xl" py="md">
      <Group justify="space-between" mb="md">
        <Title order={2}>Token Control</Title>
        <Button
          leftSection={<IconRefresh size="1rem" />}
          variant="light"
          onClick={loadTokenStatus}
          loading={loading}
        >
          Refresh
        </Button>
      </Group>

      {supabaseError && (
        <Alert icon={<IconAlertCircle size="1rem" />} color="red" radius="md" mb="md">
          Database 연결 오류: {supabaseError}. 데이터를 불러올 수 없습니다.
        </Alert>
      )}

      <Grid mb="xl">
        <Grid.Col span={12} md={6}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Title order={3}>Token Status</Title>
              <Badge
                color={paused ? 'red' : 'green'}
                leftSection={paused ? <IconPlayerPause size="0.8rem" /> : <IconPlayerPlay size="0.8rem" />}
                size="lg"
              >
                {paused ? 'Paused' : 'Active'}
              </Badge>
            </Group>
            
            <Stack>
              <Group justify="space-between">
                <Text size="sm" color="dimmed">Current Status</Text>
                <Text weight={600}>{paused ? 'Paused' : 'Active'}</Text>
              </Group>
              
              <Group justify="space-between" mt="md">
                <Button
                  leftSection={<IconPlayerPause size="1rem" />}
                  color="red"
                  variant={paused ? 'filled' : 'outline'}
                  onClick={handlePause}
                  disabled={paused}
                >
                  Pause Token
                </Button>
                <Button
                  leftSection={<IconPlayerPlay size="1rem" />}
                  color="green"
                  variant={!paused ? 'filled' : 'outline'}
                  onClick={handleUnpause}
                  disabled={!paused}
                >
                  Unpause Token
                </Button>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={12} md={6}>
          <Card shadow="sm" padding="lg" radius="md" withBorder>
            <Title order={3} mb="md">Token Statistics</Title>
            
            <Stack>
              <Group justify="space-between">
                <Text size="sm" color="dimmed">Total Supply</Text>
                <Text weight={600} size="lg">{balance}</Text>
              </Group>
              
              <Group justify="space-between">
                <Text size="sm" color="dimmed">Total Locked Amount</Text>
                <Text weight={600} size="lg">{lockedAmount}</Text>
              </Group>
              
              <Group justify="space-between">
                <Text size="sm" color="dimmed">Circulating Supply</Text>
                <Text weight={600} size="lg">
                  {(parseFloat(balance.replace(/,/g, '')) - parseFloat(lockedAmount.replace(/,/g, ''))).toLocaleString()}
                </Text>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={3}>Account Query</Title>
          <Button
            leftSection={<IconSearch size="1rem" />}
            onClick={() => setQueryModalOpened(true)}
          >
            Query Account
          </Button>
        </Group>

        {queryResult ? (
          <Stack>
            <Alert icon={<IconCheck size="1rem" />} color="green">
              Query completed successfully
            </Alert>
            <Grid>
              <Grid.Col span={12} md={4}>
                <Card padding="md" withBorder>
                  <Text size="sm" color="dimmed" mb="xs">Address</Text>
                  <Text size="sm" ff="monospace">{queryResult.address}</Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={12} md={4}>
                <Card padding="md" withBorder>
                  <Text size="sm" color="dimmed" mb="xs">Balance</Text>
                  <Text size="lg" weight={600}>{queryResult.balance}</Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={12} md={4}>
                <Card padding="md" withBorder>
                  <Text size="sm" color="dimmed" mb="xs">Locked Amount</Text>
                  <Text size="lg" weight={600}>{queryResult.lockedAmount}</Text>
                </Card>
              </Grid.Col>
            </Grid>
          </Stack>
        ) : (
          <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
            Enter an address to query balance and locked amount information.
          </Alert>
        )}
      </Card>

      <Modal
        opened={queryModalOpened}
        onClose={() => setQueryModalOpened(false)}
        title="Query Account Information"
        size="md"
      >
        <Stack>
          <TextInput
            label="Account Address"
            placeholder="0x..."
            value={queryAddress}
            onChange={(e) => setQueryAddress(e.target.value)}
            required
          />
          
          <Group justify="space-between" mt="md">
            <Button
              leftSection={<IconCoins size="1rem" />}
              onClick={handleQueryBalance}
              disabled={!queryAddress}
            >
              Query Balance
            </Button>
            <Button
              leftSection={<IconLock size="1rem" />}
              onClick={handleQueryLockedAmount}
              disabled={!queryAddress}
            >
              Query Locked Amount
            </Button>
          </Group>
          
          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={() => setQueryModalOpened(false)}>
              Close
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default TokenControl;
